import { Search, ChevronUp, ChevronDown, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import clsx from 'clsx';

/**
 * A searchable, sortable, server-paginated table.
 *
 * Every list screen in the app (customers, items, and every voucher list from
 * Phase 3 onward) is this component pointed at a different query and column
 * set, rather than a bespoke table each time.
 *
 * Wide content scrolls inside its own container (`.table-scroll`) so the page
 * body never scrolls sideways on a phone.
 *
 * @param {object[]} columns  { key, header, render?(row), sortKey?, align?, className? }
 * @param {object[]} data
 * @param {{page,pageSize,total,totalPages}} meta
 * @param {{search,sort,order,page}} query   current query state
 * @param {(next: object) => void} onQueryChange  merge-patch for query state
 */
export function DataTable({
  columns,
  data,
  meta,
  query,
  onQueryChange,
  isLoading,
  emptyLabel = 'Nothing here yet.',
  searchPlaceholder = 'Search…',
  rowKey = (row) => row.id,
  onRowClick,
}) {
  const handleSort = (col) => {
    if (!col.sortKey) return;
    const isSameColumn = query.sort === col.sortKey;
    onQueryChange({
      sort: col.sortKey,
      order: isSameColumn && query.order === 'asc' ? 'desc' : 'asc',
      page: 1,
    });
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="relative">
        <Search size={18} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted" aria-hidden="true" />
        <input
          type="search"
          value={query.search ?? ''}
          onChange={(e) => onQueryChange({ search: e.target.value, page: 1 })}
          placeholder={searchPlaceholder}
          className="h-touch w-full rounded-card border border-border bg-surface pl-10 pr-3.5 text-content placeholder:text-muted"
        />
      </div>

      <div className="card table-scroll">
        <table className="w-full min-w-[560px] border-collapse text-sm">
          <thead>
            <tr className="border-b border-border">
              {columns.map((col) => (
                <th
                  key={col.key}
                  className={clsx(
                    'whitespace-nowrap px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-muted',
                    col.align === 'right' && 'text-right',
                    col.sortKey && 'cursor-pointer select-none hover:text-content',
                  )}
                  onClick={() => handleSort(col)}
                >
                  <span className="inline-flex items-center gap-1">
                    {col.header}
                    {col.sortKey && query.sort === col.sortKey ? (
                      query.order === 'asc' ? (
                        <ChevronUp size={13} aria-hidden="true" />
                      ) : (
                        <ChevronDown size={13} aria-hidden="true" />
                      )
                    ) : null}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={columns.length} className="px-4 py-10 text-center text-muted">
                  <Loader2 size={20} className="mx-auto animate-spin" aria-hidden="true" />
                </td>
              </tr>
            ) : data.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="px-4 py-10 text-center text-muted">
                  {emptyLabel}
                </td>
              </tr>
            ) : (
              data.map((row) => (
                <tr
                  key={rowKey(row)}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  className={clsx(
                    'border-b border-border last:border-0',
                    onRowClick && 'cursor-pointer hover:bg-surface-2',
                  )}
                >
                  {columns.map((col) => (
                    <td
                      key={col.key}
                      className={clsx('tabular px-4 py-3', col.align === 'right' && 'text-right', col.className)}
                    >
                      {col.render ? col.render(row) : row[col.key]}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {meta && meta.totalPages > 1 ? (
        <div className="flex items-center justify-between gap-3 px-1 text-sm text-muted">
          <span>
            Page {meta.page} of {meta.totalPages} · {meta.total} total
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={meta.page <= 1}
              onClick={() => onQueryChange({ page: meta.page - 1 })}
              className="flex h-touch w-touch items-center justify-center rounded-card border border-border disabled:opacity-40"
              aria-label="Previous page"
            >
              <ChevronLeft size={18} aria-hidden="true" />
            </button>
            <button
              type="button"
              disabled={meta.page >= meta.totalPages}
              onClick={() => onQueryChange({ page: meta.page + 1 })}
              className="flex h-touch w-touch items-center justify-center rounded-card border border-border disabled:opacity-40"
              aria-label="Next page"
            >
              <ChevronRight size={18} aria-hidden="true" />
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default DataTable;
